import { useState, useRef, useEffect } from "react";
import { Form, Formik } from "formik";
import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
} from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useInView } from "react-intersection-observer";

import useGroupService from "../../services/api/groupService";
import useCustomerService from "../../services/api/customerService";
import { enums } from "../../constants/enum";
import {
  customerGroupCreateValidationSchema,
  groupUpdateValidationSchema,
} from "../../schemas/group";
import { useConfirm } from "../../hooks/confirm";

import {
  Badge,
  Button,
  IconButton,
  ListBox,
  Loading,
  Modal,
  PageTitle,
  Section,
  Tab,
} from "../../components";
import { TextInput } from "../../components/forms";
import {
  AddIcon,
  ArrowLongRightIcon,
  CloseIcon,
  EditIcon,
  PlusCircleIcon,
  SearchIcon,
} from "../../assets/icons";
import "./NotificationsPage.scss";

import { DeleteIcon } from "../../assets/icons";
import { CustomerListTab } from "../../components/NotificationTabs";

export default function CreateGroups() {
  const [viewingGroupId, setViewingGroupId] = useState(null);

  if (viewingGroupId) {
    return (
      <GroupDetails
        groupId={viewingGroupId}
        onBack={() => setViewingGroupId(null)}
      />
    );
  } else {
    return <CreateGroupUi setViewingGroup={setViewingGroupId} />;
  }
}

function CreateGroupUi({ setViewingGroup }) {
  const formikRef = useRef(null);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const { getAllGroups, createGroup, queryKey } = useGroupService();
  const queryClient = useQueryClient();

  const {
    data: groupsData,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: [queryKey],
    queryFn: () => getAllGroups(),
  });

  const { mutate: createGroupMutate, isPending: isCreateGroupPending } =
    useMutation({
      mutationFn: (values) => createGroup({ payload: values }),
      onSuccess: (payload) => {
        toast.success(payload.message || "Group created successfully");
        formikRef.current?.resetForm();
        setSelectedCustomers([]);
        queryClient.invalidateQueries([queryKey]);
      },
      onError: (error) => {
        toast.error(
          error.response?.data?.payload?.message || enums.DEFAULT_ERROR_MESSAGE,
        );
      },
    });

  const { groups } = groupsData || {};

  if (isError) {
    toast.error(
      error?.response?.data?.payload?.message || enums.DEFAULT_ERROR_MESSAGE,
    );
  }

  const handleAddCustomer = (customer) => {
    if (customer.id) {
      const customerId = customer.id;
      if (!selectedCustomers.find((c) => c.id === customerId)) {
        const newCustomers = [
          ...selectedCustomers,
          { ...customer, id: customerId },
        ];
        setSelectedCustomers(newCustomers);

        // Update Formik state directly
        const currentCustomers = formikRef.current?.values.customers || [];
        formikRef.current?.setFieldValue("customers", [
          ...currentCustomers,
          customerId,
        ]);
      }
    }
  };

  const handleRemoveCustomer = (id) => {
    setSelectedCustomers(selectedCustomers.filter((c) => c.id !== id));
    const currentCustomers = formikRef.current?.values.customers || [];
    formikRef.current?.setFieldValue(
      "customers",
      currentCustomers.filter((cId) => cId !== id),
    );
  };
  const handleSubmit = (values) => {
    createGroupMutate(values);
  };

  return (
    <div className="notifications-content-grid create-groups-grid">
      <Loading variant={"overlay"} isLoading={isLoading} />
      {/* Left Column */}
      <div className="contact-selection-panel">
        <Tab
          tabs={["Select Contacts"]}
          activeTab="Select Contacts"
          className="contact-tabs"
        >
          <CustomerListTab onAddCustomer={handleAddCustomer} />
        </Tab>
      </div>
      {/* Right Column */}
      <div className="create-group-right-panel">
        {/* Top Card: Create Group Form */}
        <div className="create-group-card">
          <Formik
            innerRef={formikRef}
            initialValues={{ name: "", customers: [] }}
            validationSchema={customerGroupCreateValidationSchema}
            onSubmit={handleSubmit}
          >
            <Form>
              <TextInput
                label="Group Name"
                name="name"
                placeholder="Type here"
                className="group-name-input"
              />

              <div className="selected-customers-section">
                <label>Selected Customers</label>
                <div className="chips-box">
                  {selectedCustomers.map((c, i) => (
                    <Badge
                      key={i}
                      text={c.name}
                      icon={<CloseIcon className="remove-icon" />}
                      className="customer-chip"
                      onClick={() => handleRemoveCustomer(c.id)}
                    />
                  ))}
                </div>
              </div>

              <Button
                text="Create Group"
                type="submit"
                loading={isCreateGroupPending}
                disabled={isCreateGroupPending}
              />
            </Form>
          </Formik>
        </div>

        {/* Bottom Card: Group List */}
        <Section
          wrapperClassName="group-list-card"
          className="group-list-card-content"
        >
          <div className="card-header-badge">
            <Badge text="Group List" />
          </div>
          <div className="group-list-table-container">
            <div className="group-list-table">
              <div className="table-content">
                <table>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groups?.userDefined?.customerGroups?.map((g) => (
                      <tr key={g._id}>
                        <td>{g.name}</td>
                        <td>
                          <Button
                            text="View"
                            icon={<ArrowLongRightIcon />}
                            className="view-btn"
                            onClick={() => setViewingGroup(g._id)}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </Section>
      </div>
      <Loading variant="fullScreen" isLoading={isCreateGroupPending} />
    </div>
  );
}

function GroupDetails({ groupId, onBack }) {
  const [isEditGroupNameModelOpen, setIsEditGroupNameModelOpen] =
    useState(false);
  const [isAddNewCustomersModelOpen, setIsAddNewCustomersModelOpen] =
    useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const { ref, inView } = useInView();

  const {
    getGroupById,
    deleteGroup,
    updateGroup,
    removeCustomersFromGroup,
    addCustomersToGroup,
    queryKey,
  } = useGroupService();

  const { getAllCustomers, queryKey: customerQueryKey } = useCustomerService();
  const queryClient = useQueryClient();
  const confirm = useConfirm();

  const {
    data: groupDetails,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: [queryKey, groupId],
    queryFn: () => getGroupById(groupId),
    enabled: !!groupId,
  });

  const {
    data: customersData,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: [customerQueryKey, searchQuery],
    queryFn: ({ pageParam = 1 }) => getAllCustomers(searchQuery, "", pageParam),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (lastPage?.totalPages > lastPage?.currentPage) {
        return lastPage?.currentPage + 1;
      }
      return undefined;
    },
  });

  useEffect(() => {
    if (inView) {
      fetchNextPage();
    }
  }, [inView, fetchNextPage]);

  const { isPending: isDeleteGroupPending, mutate: deleteGroupMutate } =
    useMutation({
      mutationFn: () => deleteGroup(groupId),
      onSuccess: () => {
        toast.success("Group deleted successfully");
        queryClient.invalidateQueries([queryKey]);
        onBack();
      },
      onError: (error) => {
        toast.error(
          error.response?.data?.payload?.message || enums.DEFAULT_ERROR_MESSAGE,
        );
      },
    });

  const { isPending: isUpdateGroupPending, mutate: updateGroupMutate } =
    useMutation({
      mutationFn: (values) => updateGroup({ id: groupId, payload: values }),
      onSuccess: (payload) => {
        toast.success(payload.message || "Group updated successfully");
        queryClient.invalidateQueries([queryKey]);
        setIsEditGroupNameModelOpen(false);
      },
      onError: (error) => {
        toast.error(
          error.response?.data?.payload?.message || enums.DEFAULT_ERROR_MESSAGE,
        );
      },
    });

  const { isPending: isRemoveCustomerPending, mutate: removeCustomerMutate } =
    useMutation({
      mutationFn: (customerId) =>
        removeCustomersFromGroup({
          id: groupId,
          payload: { data: { users: [customerId] } },
        }),
      onSuccess: (payload) => {
        toast.success(payload.message || "Customer removed successfully");
        queryClient.invalidateQueries([queryKey]);
      },
      onError: (error) => {
        toast.error(
          error.response?.data?.payload?.message || enums.DEFAULT_ERROR_MESSAGE,
        );
      },
    });

  const { isPending: isAddCustomerPending, mutate: addCustomerToGroupMutate } =
    useMutation({
      mutationFn: (customerId) =>
        addCustomersToGroup({
          id: groupId,
          payload: { users: [customerId] },
        }),
      onSuccess: (payload) => {
        toast.success(payload.message || "Customer added successfully");
        queryClient.invalidateQueries([queryKey]);
      },
      onError: (error) => {
        toast.error(
          error.response?.data?.payload?.message || enums.DEFAULT_ERROR_MESSAGE,
        );
      },
    });

  const handleDeleteGroup = async () => {
    const isConfirmed = await confirm({
      title: "Delete Group",
      message: "Are you sure you want to delete this group?",
    });
    if (isConfirmed) {
      deleteGroupMutate();
    }
  };

  const handleRemoveCustomerFromGroup = async (customerId) => {
    const isConfirmed = await confirm({
      title: "Remove Customer",
      message: "Are you sure you want to remove this customer from the group?",
      confirmText: "Remove",
    });
    if (isConfirmed) {
      removeCustomerMutate(customerId);
    }
  };

  const handleAddCustomerToGroup = (customerId) => {
    addCustomerToGroupMutate(customerId);
  };

  if (isError) {
    toast.error(
      error?.response?.data?.payload?.message || enums.DEFAULT_ERROR_MESSAGE,
    );
  }

  const group = groupDetails || {};
  const availableCustomers =
    customersData?.pages.flatMap((page) => page.customers) || [];

  return (
    <>
      <div className="group-details-panel">
        <Loading
          variant={
            isDeleteGroupPending ||
            isUpdateGroupPending ||
            isRemoveCustomerPending ||
            isAddCustomerPending
              ? "fullScreen"
              : "overlay"
          }
          isLoading={
            isLoading ||
            isDeleteGroupPending ||
            isUpdateGroupPending ||
            isRemoveCustomerPending ||
            isAddCustomerPending
          }
        />
        {/* header  */}
        <PageTitle text="Group Details" prev={() => onBack()}>
          <div className="action-list-wide">
            <Button
              text="Add New Customers"
              icon={<AddIcon />}
              onClick={() => setIsAddNewCustomersModelOpen(true)}
            />
            <IconButton
              icon={<DeleteIcon />}
              rounded
              onClick={handleDeleteGroup}
            />
          </div>

          <ListBox
            className="action-list"
            options={[
              {
                text: "Add new Customers",
                icon: <AddIcon />,
                onClick: () => setIsAddNewCustomersModelOpen(true),
              },
              {
                text: "Delete Group",
                icon: <DeleteIcon />,
                onClick: handleDeleteGroup,
              },
            ]}
          />
        </PageTitle>

        <Section className="data-sec">
          <div className="left-bx">
            <div className="name group-name">
              <span>Name</span>
              <div className="name-bx">
                <h2>{group.name}</h2>{" "}
                <IconButton
                  icon={<EditIcon />}
                  className="edit-icon"
                  onClick={() => setIsEditGroupNameModelOpen(true)}
                />
              </div>
            </div>
          </div>

          <div className="right-bx">
            <div className="location">
              <span>Customer count</span>
              <p>{group.customers?.length || 0}</p>
            </div>
          </div>
        </Section>

        <Section
          wrapperClassName="customers-section-wrapper"
          className="customers-section"
        >
          <h1>Customer List</h1>
          <Section>
            <div className="customer-list">
              <div className="table-title">
                <h3>Customers</h3>
              </div>

              <div className="table-content">
                <table>
                  <thead>
                    <tr>
                      <th>Customer Id</th>
                      <th>Name</th>
                      <th>Mobile Number</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group?.customers?.map((customer, index) => (
                      <tr key={customer._id || index}>
                        <td>{index + 1}</td>
                        <td>{customer.name}</td>
                        <td>{customer.mobileNumber}</td>
                        <td>
                          <Button
                            text="Remove"
                            icon={<CloseIcon />}
                            className="action-btn"
                            onClick={() =>
                              handleRemoveCustomerFromGroup(customer._id)
                            }
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </Section>
        </Section>
      </div>

      <Modal
        containerClassName="edit-group-name-modal"
        close={!isEditGroupNameModelOpen}
        onClose={() => setIsEditGroupNameModelOpen(false)}
        title="Edit Group Name"
      >
        <Section>
          <Formik
            initialValues={{ name: group.name }}
            enableReinitialize
            validationSchema={groupUpdateValidationSchema}
            onSubmit={(values) => updateGroupMutate(values)}
          >
            <Form>
              <TextInput
                label="Group Name"
                name="name"
                placeholder="Type here"
                className="group-name-input"
              />
              <Button
                text="Update Group Name"
                icon={<EditIcon />}
                type="submit"
                loading={isUpdateGroupPending}
                disabled={isUpdateGroupPending}
              />
            </Form>
          </Formik>
        </Section>
      </Modal>
      <Modal
        containerClassName="add-new-customers-modal"
        close={!isAddNewCustomersModelOpen}
        onClose={() => setIsAddNewCustomersModelOpen(false)}
        title="Add New Customers"
      >
        <Section>
          <div className="contact-list-container">
            <div className="search-bar">
              <Formik
                initialValues={{ search: "" }}
                onSubmit={(values) => setSearchQuery(values.search)}
              >
                <Form>
                  <TextInput
                    name="search"
                    placeholder="Enter customer name"
                    className="search-input"
                  />
                  <IconButton
                    type="submit"
                    icon={<SearchIcon className="search-icon" />}
                  />
                </Form>
              </Formik>
            </div>

            <div className="contact-list">
              <div className="table-content">
                <table>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {availableCustomers.map((contact) => (
                      <tr key={contact._id || contact.id}>
                        <td>
                          {contact.name ||
                            `${contact.firstName} ${contact.lastName}`}
                        </td>
                        <td>
                          <Button
                            text="Add"
                            icon={<PlusCircleIcon />}
                            onClick={() =>
                              handleAddCustomerToGroup(
                                contact._id || contact.id,
                              )
                            }
                            className="action-btn"
                            loading={isAddCustomerPending}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div
                  ref={ref}
                  className="load-more-container"
                  style={{ textAlign: "center", padding: "10px" }}
                >
                  {isFetchingNextPage ? <Loading variant="inline" /> : null}
                </div>
              </div>
            </div>
          </div>
        </Section>
      </Modal>
    </>
  );
}
